import Link from "next/link";
import { type JSX } from "react";
import {
  SitemapNavigationProps,
  SitemapNode,
} from "components/sitemap-navigation/sitemap-navigation.props";
import { NoDataFallback } from "../../utils/NoDataFallback";
import SitemapAccordion, { type AccordionItem } from "./SitemapAccordion";
import { useTranslations } from "next-intl";
import { firstNonEmpty } from "src/utils";

function toArray(items: SitemapNode[] | object | undefined) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (items && typeof items === "object") return Object.values(items);
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSitemapRoots(data: any): SitemapNode[] {
  if (Array.isArray(data?.fields)) {
    return data.fields as SitemapNode[];
  }
  // fallback if structure differs
  if (Array.isArray(data)) {
    return data as SitemapNode[];
  }
  return [];
}

const DEFAULT_NODE_TITLE = "Untitled";

const getNodeUrl = (node: SitemapNode): string => {
  const href = node?.Href || "";
  const qs = node?.Querystring?.trim();
  if (!qs) return href;
  return qs.startsWith("?") ? `${href}${qs}` : `${href}?${qs}`;
};

interface SitemapTreeProps {
  nodes: SitemapNode[];
  level?: number;
}

const SitemapTree = ({
  nodes,
  level = 1,
}: SitemapTreeProps): JSX.Element | null => {
  if (!nodes?.length) return null;

  let listClass = "list-[square]";

  if (level === 1) {
    listClass = "list-[disc]";
  } else if (level === 2) {
    listClass = "list-[circle]";
  }

  return (
    <ul className={`pl-3.75 ${listClass}`}>
      {nodes.map((node) => {
        const title =
          firstNonEmpty(
            node?.NavigationTitle?.value,
            node?.NavigationTitle?.editable,
            node?.DisplayName,
          ) ?? DEFAULT_NODE_TITLE;
        const url = getNodeUrl(node);
        const children = node.Children ?? [];
        const hasChildren = children.length > 0;

        return (
          <li
            key={node.Id}
            className={`text-sm m-0 ${url ? "" : "list-none [&>li]:list-disc"}`}
          >
            {url ? (
              <Link
                href={url}
                className="text-sm text-text-gray [&:hover]:text-black focus:text-black focus:font-medium focus:no-underline active:outline-[none] outline-offset-3 underline"
              >
                {title}
              </Link>
            ) : (
              <h3 className="text-[16px] leading-[1.1] mt-5 ml-3.75 mb-1.25 text-text-gray">
                {title}
              </h3>
            )}

            {hasChildren && (
              <div className={level === 1 && !url ? "[&>ul>li]:list-disc" : ""}>
                <SitemapTree nodes={children} level={level + 1} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const Default = (props: SitemapNavigationProps): JSX.Element => {
  console.log("---->[SitemapNavigation] Component", props.fields);

  const { fields, params } = props;
  const roots = getSitemapRoots(toArray(fields));
  const t = useTranslations();

  if (roots.length === 0) {
    return <NoDataFallback componentName="Sitemap Navigation" />;
  }

  const items: AccordionItem[] = roots.map((node) => ({
    id: node.Id,
    title:
      firstNonEmpty(
        node?.NavigationTitle?.value,
        node?.NavigationTitle?.editable,
        node?.DisplayName,
      ) ?? DEFAULT_NODE_TITLE,
    content: <SitemapTree nodes={node.Children ?? []} level={1} />,
  }));

  return (
    <section
      className="w-full mb-(--global-component-spacing) px-4 mx-auto"
      id={params?.RenderingIdentifier || undefined}
      data-testid="sitemap-navigation-component"
    >
      {t.has("ecolab-sitemap-heading") && (
        <h1 className="text-[26px] leading-[1.1] text-text-gray">
          {t("ecolab-sitemap-heading")}
        </h1>
      )}
      <SitemapAccordion items={items} multiple />
    </section>
  );
};
